// Assumptions 탭 — 바닐라 fillForm/readForm 중 핵심 편집 필드 이식.
// 기본 가정(회사명·사업내용·평가기준일·결산월·추정기간·법인세율·WACC 직접입력·
// 영구성장률) + 자본구조 핵심(비영업자산·이자부부채·발행주식수).
// 추정 드라이버·과거 손익·NWC 는 Projection 탭, WACC 모듈 산출은 WACC 탭에서 편집.
// 나머지 상세 가정(스톡옵션·NOL·우선주 등)은 기존 화면(/dcf)에서 편집.
//
// 입력 방식: 바닐라의 fillForm(payload→input)/readForm(input→payload)과 동일하게
// 비제어 입력 + blur 시 커밋. key={value} 로 payload 변경 시 표시값을 재초기화한다.
// (재계산/저장 버튼 클릭 전에 blur 가 먼저 발생하므로 payload 는 항상 최신)
// 에이전트 dcf_patch 가 건드린 필드는 cell-flash 로 1.5초 하이라이트 (바닐라 applyDcfPatch fieldMap).
import { useState } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { fmtInput, parseNum } from "../../lib/format";
import { unitMoney, useT } from "../../lib/i18n";
import { Button } from "../../ui/Button";
import { pathsInclude, useDcf, usesWaccModule } from "../../stores/dcf";

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="wacc-row">
      <span className="wacc-label">{label}</span>
      {children}
    </div>
  );
}

type FieldProps = {
  value: string;
  onCommit: (v: string) => void;
  /** 에이전트 patch 대상 — cell-flash. key 에 flashSeq 를 섞어 값이 같아도 재발화 */
  flash?: boolean;
  flashSeq?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onBlur" | "defaultValue">;

function Field({ value, onCommit, flash, flashSeq, ...rest }: FieldProps) {
  return (
    <input
      key={flash ? `${value}::f${flashSeq}` : value}
      className={`wacc-input${flash ? " cell-flash" : ""}`}
      defaultValue={value}
      onBlur={(e) => {
        if (e.target.value !== value) onCommit(e.target.value);
      }}
      {...rest}
    />
  );
}

export default function AssumptionsTab({ onGoWacc }: { onGoWacc?: () => void }) {
  const { lang, t } = useT();
  const current = useDcf((s) => s.current);
  const setPayloadField = useDcf((s) => s.setPayloadField);
  const setDiscountRateDirect = useDcf((s) => s.setDiscountRateDirect);
  const recalc = useDcf((s) => s.recalc);
  const lastPatchPaths = useDcf((s) => s.lastPatchPaths);
  const patchSeq = useDcf((s) => s.patchSeq);
  // 접이식 그룹 — 값이 있으면 펼친 채로 시작. 훅은 early-return 보다 먼저
  // 호출돼야 하므로(rules-of-hooks) 초기값은 스토어에서 직접 읽는다.
  const [prefOpen, setPrefOpen] = useState<boolean>(() => {
    const n0 = useDcf.getState().current?.payload.non_operating || {};
    return !!(n0.preferred_stock_amount || n0.preferred_stock_shares != null);
  });
  const [soOpen, setSoOpen] = useState<boolean>(() => {
    const n0 = useDcf.getState().current?.payload.non_operating || {};
    return (
      n0.stock_option_shares != null ||
      n0.stock_option_exercise_price != null ||
      n0.stock_option_current_price != null
    );
  });
  if (!current) return null;
  const flashOf = (path: string) => pathsInclude(lastPatchPaths, path);
  const p = current.payload;
  const no = p.non_operating || {};
  // 완전희석 자동 계산 — 엔진(dcf.py 희석 산출)과 같은 식: 보통주 + 우선주 전환 +
  // 스톡옵션 Treasury Stock Method (참조가액 없으면 산출 주당가치 사용)
  const dil = (() => {
    const common = Number(no.shares_outstanding || 0);
    const pref = Number(no.preferred_stock_shares || 0);
    const so = Number(no.stock_option_shares || 0);
    const strike = Number(no.stock_option_exercise_price || 0);
    const refP =
      Number(no.stock_option_current_price || 0) ||
      Number(current.result?.bridge?.price_per_share || 0);
    const netSo =
      so > 0 && strike > 0 && refP > strike ? Math.floor(so * (1 - strike / refP)) : 0;
    return { common, pref, netSo, total: common + pref + netSo };
  })();
  const autoDiluted = dil.total;
  const dilOverride = no.diluted_shares_outstanding as number | null | undefined;
  const waccModule = usesWaccModule(p);
  // 바닐라 fillForm: WACC 모듈 프로젝트는 산출값(result.wacc.value) 우선 표시
  const waccDisp = waccModule
    ? (current.result?.wacc?.value ?? p.discount_rate)
    : p.discount_rate;

  return (
    <>
      <div className="data-card">
        <h4>{t("h.basic_assumptions")}</h4>
        <Row label={t("lbl.company")}>
          <Field
            value={p.target_name || ""}
            flash={flashOf("target_name")}
            flashSeq={patchSeq}
            onCommit={(v) => {
              const t = v.trim();
              if (t) setPayloadField("target_name", t);
            }}
          />
        </Row>
        <div className="wacc-row" style={{ alignItems: "flex-start" }}>
          <span className="wacc-label">{t("lbl.biz_desc")}</span>
          <textarea
            key={p.business_description || ""}
            className="wacc-input"
            rows={3}
            style={{
              width: 440,
              maxWidth: "100%",
              height: 72,
              textAlign: "left",
              resize: "vertical",
              minHeight: 72,
              lineHeight: 1.5,
            }}
            placeholder={t("ph.biz_desc")}
            defaultValue={p.business_description || ""}
            onBlur={(e) => {
              const t = e.target.value.trim();
              if (t === (p.business_description || "")) return;
              setPayloadField("business_description", t || undefined);
            }}
          />
        </div>
        <Row label={t("lbl.val_date")}>
          <Field
            value={p.valuation_date || ""}
            flash={flashOf("valuation_date")}
            flashSeq={patchSeq}
            onCommit={(v) => {
              if (v.trim()) setPayloadField("valuation_date", v.trim());
            }}
          />
        </Row>
        <Row label={t("lbl.fye")}>
          <Field
            value={String(p.fiscal_year_end_month || 12)}
            flash={flashOf("fiscal_year_end_month")}
            flashSeq={patchSeq}
            onCommit={(v) =>
              setPayloadField("fiscal_year_end_month", parseInt(v, 10) || 12)
            }
          />
        </Row>
        <Row label={t("lbl.forecast_yrs")}>
          <Field
            value={String(p.forecast_full_years || 5)}
            flash={flashOf("forecast_full_years")}
            flashSeq={patchSeq}
            onCommit={(v) =>
              setPayloadField("forecast_full_years", parseInt(v, 10) || 5)
            }
          />
        </Row>
        <Row label={t("lbl.tax_rate")}>
          <Field
            value={((p.tax_rate || 0) * 100).toFixed(1)}
            flash={flashOf("tax_rate")}
            flashSeq={patchSeq}
            onCommit={(v) => setPayloadField("tax_rate", parseNum(v) / 100)}
          />
        </Row>
        <Row
          label={
            <>
              {t("lbl.wacc")}{" "}
              {/* WACC 탭 바로가기 — 모듈 산출(peer 기반)로 갈아타는 자리가 저기다 */}
              <button
                type="button"
                className="wacc-jump"
                title={t("btn.goto_wacc")}
                aria-label={t("btn.goto_wacc")}
                onClick={onGoWacc}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7" />
                  <path d="M8 7h9v9" />
                </svg>
              </button>
              {waccModule && <span className="hint">{t("hint.wacc_module")}</span>}
            </>
          }
        >
          <Field
            value={((waccDisp || 0) * 100).toFixed(2)}
            flash={flashOf("discount_rate")}
            flashSeq={patchSeq}
            onCommit={(v) => setDiscountRateDirect(parseNum(v) / 100)}
          />
        </Row>
        <Row label={t("lbl.tgr")}>
          <Field
            value={((p.terminal_growth_rate || 0) * 100).toFixed(2)}
            flash={flashOf("terminal_growth_rate")}
            flashSeq={patchSeq}
            onCommit={(v) => setPayloadField("terminal_growth_rate", parseNum(v) / 100)}
          />
        </Row>
      </div>

      <div className="data-card">
        <h4>{t("h.capital_core")}</h4>
        <Row label={`${t("lbl.non_op")} (${unitMoney(lang)})`}>
          <Field
            value={fmtInput(no.non_operating_assets)}
            flash={flashOf("non_operating.non_operating_assets")}
            flashSeq={patchSeq}
            onCommit={(v) =>
              setPayloadField("non_operating.non_operating_assets", parseNum(v))
            }
          />
        </Row>
        <Row label={`${t("lbl.debt")} (${unitMoney(lang)})`}>
          <Field
            value={fmtInput(no.interest_bearing_debt)}
            flash={flashOf("non_operating.interest_bearing_debt")}
            flashSeq={patchSeq}
            onCommit={(v) =>
              setPayloadField("non_operating.interest_bearing_debt", parseNum(v))
            }
          />
        </Row>
        <Row label={t("lbl.shares")}>
          <Field
            value={fmtInput(no.shares_outstanding)}
            flash={flashOf("non_operating.shares_outstanding")}
            flashSeq={patchSeq}
            onCommit={(v) =>
              setPayloadField("non_operating.shares_outstanding", parseNum(v))
            }
          />
        </Row>
        {/* 우선주·스톡옵션 — 입력 템플릿 '회사정보' 시트와 동일 항목. 접이식 그룹:
            값이 있으면 펼친 채로 시작한다. 전부 선택 입력이라 비우면 payload 에서
            키를 제거한다(0 과 미입력은 다르다 — 주식수 0 은 "없음 확정",
            미입력은 "희석 계산 생략"). */}
        {(
          [
            {
              titleKey: "grp.pref",
              open: prefOpen,
              toggle: () => setPrefOpen((v: boolean) => !v),
              fields: [
                ["lbl.pref_amount", "preferred_stock_amount", false],
                ["lbl.pref_shares", "preferred_stock_shares", true],
              ] as Array<[string, string, boolean]>,
            },
            {
              titleKey: "grp.so",
              open: soOpen,
              toggle: () => setSoOpen((v: boolean) => !v),
              fields: [
                ["lbl.so_shares", "stock_option_shares", true],
                ["lbl.so_strike", "stock_option_exercise_price", true],
                ["lbl.so_ref_price", "stock_option_current_price", true],
              ] as Array<[string, string, boolean]>,
            },
          ]
        ).map((g) => (
          <div className="cap-group" key={g.titleKey}>
            <button
              type="button"
              className="cap-group-head"
              aria-expanded={g.open}
              onClick={g.toggle}
            >
              <span className="cap-group-sign">{g.open ? "−" : "+"}</span>
              {t(g.titleKey)}
            </button>
            {g.open &&
              g.fields.map(([labelKey, key, optional]) => (
                <Row label={t(labelKey)} key={key}>
                  <Field
                    value={no[key] == null ? "" : fmtInput(no[key] as number)}
                    flash={flashOf(`non_operating.${key}`)}
                    flashSeq={patchSeq}
                    onCommit={(v) =>
                      setPayloadField(
                        `non_operating.${key}`,
                        v.trim() === "" ? (optional ? undefined : 0) : parseNum(v),
                      )
                    }
                  />
                </Row>
              ))}
          </div>
        ))}
        {/* 완전희석 — 자동 계산값을 선채워 보여주고, 클릭해서 고치면 그 값이
            우선한다(엔진과 같은 우선순위). 자동값과 같거나 비우면 override 해제. */}
        <Row
          label={
            <>
              {t("lbl.fd_shares_auto")}{" "}
              {dilOverride != null && dilOverride !== autoDiluted && (
                <span className="hint">({t("fd.override")})</span>
              )}
            </>
          }
        >
          <Field
            value={fmtInput(dilOverride != null ? dilOverride : autoDiluted)}
            flash={flashOf("non_operating.diluted_shares_outstanding")}
            flashSeq={patchSeq}
            onCommit={(v) => {
              const n = v.trim() === "" ? autoDiluted : parseNum(v);
              // 자동값 그대로면 override 를 남기지 않는다 — 입력이 바뀌면 자동 추종 유지
              setPayloadField(
                "non_operating.diluted_shares_outstanding",
                n === autoDiluted ? undefined : n,
              );
            }}
          />
        </Row>
        {/* 산출 수식 — 어떻게 나온 숫자인지 한 줄로 */}
        <p className="fd-formula">
          = {t("fd.common")} {dil.common.toLocaleString("ko-KR")}
          {dil.pref > 0 && <> + {t("fd.pref")} {dil.pref.toLocaleString("ko-KR")}</>}
          {dil.netSo > 0 && <> + {t("fd.so")} {dil.netSo.toLocaleString("ko-KR")}</>}
          {" = "}
          {autoDiluted.toLocaleString("ko-KR")}
          {dil.netSo > 0 && (
            <span className="hint"> · TSM: 옵션 × (1 − 행사가/참조가)</span>
          )}
        </p>
      </div>

      <div
        style={{ display: "flex", gap: 10, marginTop: 4, alignItems: "center", flexWrap: "wrap" }}
      >
        <Button variant="primary" onClick={() => void recalc()}>
          {t("btn.save_recalc")}
        </Button>
        <span className="hint">
          {t("hint.assump_more")}
          <a href="/dcf">{t("hint.assump_more_link")}</a>
        </span>
      </div>
    </>
  );
}
